import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import If from '@components/if/if.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import ChartEmpty from '@shared/components/charts/chart-empty/chart-empty.tsx';
import { BarChartProps } from '@shared/models/charts/bar-chart.model.ts';

const BarChart = <T extends Record<string, unknown>>({
  data,
  loading = false,
  config
}: BarChartProps<T>) => {
  const { t } = useTranslation();
  const {
    xAxisKey,
    dataKey,
    height = 300,
    color = 'var(--teal)',
    barSize = 20,
    barRadius = [10, 10, 10, 10],
    barGap = 8,
    showGrid = true,
    legendLabel,
    yAxisFormatter,
    tooltipFormatter,
    tooltipValueLabel,
    xAxisFormatter
  } = config;

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{
        boxShadow: '0 4px 12px var(--black-alpha-50)'
      }}
    >
      <If
        loading={loading}
        condition={!loading && data.length > 0}
        loadingContent={<ChartLoader height={height} />}
        emptyContent={<ChartEmpty height={height} />}
      >
        {legendLabel && (
          <div className="flex items-center mb-4 ml-4 h-8.5">
            <div
              className="w-2.5 h-2.5 rounded-[50%] mr-2"
              style={{ backgroundColor: color }}
            />
            <span className="text-[14px] font-normal text-(--blue-4)">
              {legendLabel}
            </span>
          </div>
        )}

        <ResponsiveContainer width="100%" height={height}>
          <RechartsBarChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            barGap={barGap}
          >
            {showGrid && (
              <CartesianGrid vertical={false} stroke="var(--gray-7)" />
            )}
            <XAxis
              dataKey={xAxisKey as string}
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickFormatter={xAxisFormatter}
              tickMargin={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickFormatter={yAxisFormatter}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: 'none',
                boxShadow: '0 0 10px var(--black-alpha-100)'
              }}
              labelStyle={{ color: 'var(--dark-2)' }}
              formatter={
                tooltipValueLabel || tooltipFormatter
                  ? (value) => {
                      const formatted = tooltipFormatter
                        ? tooltipFormatter(value as number)
                        : value;
                      return tooltipValueLabel
                        ? [formatted, t(tooltipValueLabel)]
                        : formatted;
                    }
                  : undefined
              }
            />
            <Bar
              dataKey={dataKey as string}
              fill={color}
              radius={barRadius}
              barSize={barSize}
            />
          </RechartsBarChart>
        </ResponsiveContainer>
      </If>
    </div>
  );
};

export default BarChart;
